
import { Routes, Route, Link } from 'react-router-dom';
import AdvancedAnalytics from './AdvancedAnalytics';
import TacticalAnalysis from '../../components/Tactical/TacticalAnalysis';
import { useTranslation } from 'react-i18next';
import PlayerDetailPage from './PlayerDetailPage';
import PlayerIntelligenceDashboard from './PlayerIntelligenceDashboard';
import ContractMonitorPage from './ContractMonitorPage';
import ReportesJugadoresListPage from './ReportesJugadoresListPage';
import ReporteJugadorFormPage from './ReporteJugadorFormPage';
import ReporteJugadorDetailPage from './ReporteJugadorDetailPage';
import CountryExplorerPage from './CountryExplorerPage';
import CountryPlayersPage from './CountryPlayersPage';
import PlayerComparisonPage from './PlayerComparisonPage';
import {
  Target,
  Clock,
  ArrowRight,
  Brain,
  Activity,
  BarChart3,
  TrendingUp,
  Search,
  FileText,
  Globe,
  GitCompare
} from 'lucide-react';

const SportingPage: React.FC = () => {
  const { t } = useTranslation();

  const sportingModules = [
    {
      id: 'player-search-ai',
      title: t('sporting.playerSearchIA'),
      description: t('sporting.playerSearchIADescription'),
      icon: Brain,
      gradient: 'from-purple-600 via-violet-700 to-indigo-800',
      route: '/dashboard',
      comingSoon: false
    },
    {
      id: 'player-comparison',
      title: t('sporting.playerComparison'),
      description: t('sporting.playerComparisonDescription'),
      icon: GitCompare,
      gradient: 'from-indigo-600 via-blue-700 to-cyan-800',
      route: '/sporting/player-comparison',
      comingSoon: false
    },
    {
      id: 'expiring-contracts',
      title: t('sporting.marketOpportunities'),
      description: t('sporting.expiringContractsDescription'),
      icon: Clock,
      gradient: 'from-yellow-600 via-orange-700 to-red-800',
      route: '/sporting/expiring-contracts',
      comingSoon: false
    },
    {
      id: 'reportes-jugadores',
      title: 'Reportes de Jugadores',
      description: 'Crea y gestiona reportes detallados de scouting de jugadores',
      icon: FileText,
      gradient: 'from-blue-600 via-cyan-700 to-teal-800',
      route: '/sporting/reportes-jugadores',
      comingSoon: false
    },
    {
      id: 'country-explorer',
      title: 'Explorador por Nacionalidad',
      description: 'Descubre jugadores organizados por país de nacimiento con estadísticas detalladas',
      icon: Globe,
      gradient: 'from-green-600 via-emerald-700 to-teal-800',
      route: '/sporting/countries',
      comingSoon: false
    }
  ];

  // Función helper para obtener clases de color
  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; text: string }> = {
      blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
      green: { bg: 'bg-green-100', text: 'text-green-600' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-600' }
    };
    return colorMap[color] || { bg: 'bg-gray-100', text: 'text-gray-600' };
  };

  return (
    <Routes>
      <Route path="/" element={
        <div className="space-y-12 animate-fade-in">
          {/* Header */}
          <div className="relative p-10 text-center space-y-6 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white shadow-xl rounded-2xl border border-blue-500/20">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 to-blue-800/90 rounded-2xl"></div>
            <div className="relative z-10">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {t('sporting.title')} <span className="text-blue-100">{t('sporting.directionHighlight')}</span>
              </h1>
              <p className="text-lg text-blue-100 max-w-3xl mx-auto leading-relaxed">
                {t('sporting.subtitle')} • {t('sporting.advancedToolsText')}
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {[
              { label: t('sporting.playersObserved'), value: '0', icon: Search, color: 'blue' },
              { label: t('sporting.marketOpportunities'), value: '0', icon: Clock, color: 'orange' }
            ].map((stat, index) => {
              const Icon = stat.icon;
              const colors = getColorClasses(stat.color);
              return (
                <div
                  key={stat.label}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 group"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</p>
                  <p className="text-gray-600 text-sm">{stat.label}</p>
                </div>
              );
            })}
          </div>

          {/* Modules Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {sportingModules.map((module, index) => {
              const Icon = module.icon;
              return (
                <Link
                  key={module.id}
                  to={module.comingSoon ? '#' : module.route}
                  className={`group relative overflow-hidden bg-white rounded-3xl shadow-lg border border-gray-200 hover:border-gray-300 transition-all duration-300 ${
                    module.comingSoon 
                      ? 'cursor-not-allowed opacity-75' 
                      : 'hover:shadow-xl hover:-translate-y-2 cursor-pointer'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={(e) => module.comingSoon && e.preventDefault()}
                >
                  {/* Background Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${module.gradient} opacity-0 ${!module.comingSoon ? 'group-hover:opacity-5' : ''} transition-opacity duration-500 rounded-3xl`} />

                  {/* Coming Soon Badge */}
                  {module.comingSoon && (
                    <div className="absolute top-4 right-4 bg-gray-100 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full border border-gray-200">
                      {t('sporting.comingSoon')}
                    </div>
                  )}

                  {/* Content */}
                  <div className="relative p-8">
                    {/* Icon */}
                    <div className={`w-16 h-16 bg-gradient-to-br ${module.gradient} rounded-2xl flex items-center justify-center mb-6 ${!module.comingSoon ? 'group-hover:scale-110 group-hover:rotate-3' : ''} transition-all duration-500 shadow-lg`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>

                    {/* Title and Description */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                      {module.title}
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                      {module.description}
                    </p>

                    {/* Action */}
                    <div className="flex items-center justify-between">
                      <div className={`inline-flex items-center space-x-2 text-sm font-medium transition-colors duration-300 ${
                        module.comingSoon ? 'text-gray-500' : 'text-blue-600 group-hover:text-blue-700'
                      }`}>
                        <span>{module.comingSoon ? t('sporting.comingSoon') : t('sporting.access')}</span>
                        {!module.comingSoon && (
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Hover Effect Border Glow */}
                  {!module.comingSoon && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 via-purple-400/10 to-indigo-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Featured Section - AI Player Search */}
          <div className="bg-gradient-to-br from-purple-600 via-violet-700 to-indigo-800 rounded-3xl p-8 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10" />
            <div className="relative z-10">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">{t('sporting.aiPlayerSearch')}</h3>
                  <p className="text-white/80">{t('sporting.aiPlayerSearchSubtitle')}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
                  <Activity className="w-8 h-8 text-white mb-4" />
                  <h4 className="font-semibold mb-2">{t('sporting.naturalSearch')}</h4>
                  <p className="text-sm text-white/80">{t('sporting.naturalSearchDescription')}</p>
                </div>
                <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
                  <BarChart3 className="w-8 h-8 text-white mb-4" />
                  <h4 className="font-semibold mb-2">{t('sporting.intelligentAnalysis')}</h4>
                  <p className="text-sm text-white/80">{t('sporting.intelligentAnalysisDescription')}</p>
                </div>
                <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
                  <TrendingUp className="w-8 h-8 text-white mb-4" />
                  <h4 className="font-semibold mb-2">{t('sporting.preciseResults')}</h4>
                  <p className="text-sm text-white/80">{t('sporting.preciseResultsDescription')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      } />
      
      {/* Placeholder routes for future modules */}
      <Route path="/advanced-analytics" element={<AdvancedAnalytics />} />
      <Route path="/field-maps" element={<TacticalAnalysis />} />
      <Route path="/player-detail/:playerId" element={<PlayerDetailPage />} />
      <Route path="/player-intelligence/:playerId" element={<PlayerIntelligenceDashboard />} />
      <Route path="/player-comparison" element={<PlayerComparisonPage />} />
      <Route path="/expiring-contracts" element={<ContractMonitorPage />} />
      <Route path="/reportes-jugadores" element={<ReportesJugadoresListPage />} />
      <Route path="/reportes-jugadores/new" element={<ReporteJugadorFormPage />} />
      <Route path="/reportes-jugadores/:reportId" element={<ReporteJugadorDetailPage />} />
      <Route path="/countries" element={<CountryExplorerPage />} />
      <Route path="/countries/:country" element={<CountryPlayersPage />} />
      
      <Route path="/*" element={
        <div className="text-center py-16">
          <Target className="w-20 h-20 text-neutral-400 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-neutral-100 mb-4">{t('sporting.moduleInDevelopment')}</h2>
          <p className="text-neutral-300 mb-8">{t('sporting.moduleComingSoon')}</p>
          <Link to="/sporting" className="btn-primary">
            <ArrowRight className="w-4 h-4 mr-2" />
            {t('sporting.backToSporting')}
          </Link>
        </div>
      } />
    </Routes>
  );
};

export default SportingPage;
